import { ErrorView, Loader } from '@percent/cause-dashboard/common/components'
import { useAuthState, useDidMountEffect, useQueryList } from '@percent/cause-dashboard/common/hooks'
import { dayJS, getFormattedDate } from '@percent/cause-dashboard/common/utility/date'
import {
  formatAmount,
  formatMinorUnitsValueToFullNumberWithDecimals
} from '@percent/cause-dashboard/common/utility/money/formatAmount'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import type { Donation, DonationStatus } from '@percent/cause-dashboard/services/donations/donationsService.types'
import {
  Alert,
  Badge,
  Button,
  Copy,
  DateRange,
  Grid,
  Icon,
  Menu,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip,
  Typography
} from '@percent/lemonade'
import type { TableColumn } from '@percent/lemonade'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useTranslation } from 'react-i18next'
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params'

import { DonationsPageHelpLinks } from '@percent/cause-dashboard/app/donations/donationsPageHelpLinks/DonationsPageHelpLinks'
import { useCSVDownloader } from '@percent/cause-dashboard/common/hooks/useCSVDownloader/useCSVDownloader'
import { csvFileName } from '@percent/cause-dashboard/common/utility/csvFileName'
import type { MouseEvent } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'
import styles from './DonationsPage.module.scss'
import { useBreadcrumbs } from '../layout/breadcrumbsContext/useBreadcrumbs/useBreadcrumbs'
import { RoutePath } from '@percent/cause-dashboard/app/routes/Routes'
import { useNavigate } from 'react-router-dom'
import { Checkbox } from '@percent/lemonade'

const EMPTY_RESULT_STATES = {
  NO_DONATIONS: 'no-donations',
  NO_RESULTS: 'no-results'
} as const

const DONATION_FILTER_STATES = {
  DATE_ONLY: 'dateOnly',
  DATE_AND_GIFT_NOTE: 'dateAndGiftNote',
  GIFT_NOTE_ONLY: 'giftNoteOnly',
  NONE: 'none'
} as const

export const DonationsPage = () => {
  const { t } = useTranslation()
  useBreadcrumbs([{ label: t('breadcrumb.donations') }])

  const {
    donationsService,
    reportingService: { getDonationReport }
  } = useServices()
  const {
    currencyInfo,
    authState: { user }
  } = useAuthState()
  const anchorEl = useRef<HTMLElement | null>(null)
  const [isDatePickerOpened, setIsDatePickerOpened] = useState(false)
  const [queryParams, setQueryParams] = useQueryParams({
    pageSize: withDefault(NumberParam, 25),
    startDate: StringParam,
    endDate: StringParam
  })
  const navigate = useNavigate()

  const donationStatusI18nKey = {
    PLEDGED: 'typography.donationStatus.pledged.title',
    PAID: 'typography.donationStatus.paid.title'
  } as const

  const getStatusBadge = (status: DonationStatus) => {
    if (!status) return null
    return (
      <Badge variant={status === 'PAID' ? 'positive' : 'default'} icon={status === 'PAID' ? 'approve' : 'payout'}>
        {t(donationStatusI18nKey[status])}
      </Badge>
    )
  }

  const formatDateParams = useCallback(
    <T extends string | null | undefined>(startDate: T, endDate: T) => {
      const isEndDateInTheFuture = endDate ? dayJS(endDate).isAfter(dayJS()) : false

      return {
        startDate: startDate ? dayJS(queryParams.startDate).toISOString() : undefined,
        endDate: endDate && !isEndDateInTheFuture ? dayJS(endDate).toISOString() : dayJS().toISOString()
      } as { startDate: T; endDate: T }
    },
    [queryParams.startDate]
  )

  const [showDonationsWithGiftNotes, setShowDonationsWithGiftNotes] = useState(false)

  const donationQueryParams = useMemo(
    () => ({
      ...queryParams,
      ...formatDateParams(queryParams.startDate, queryParams.endDate),
      hasGiftNote: showDonationsWithGiftNotes || undefined
    }),
    [queryParams, showDonationsWithGiftNotes, formatDateParams]
  )

  const [{ data, totalResults, isLoading, errorMessage }, { query, nextPage, previousPage }] = useQueryList(
    donationsService.getDonationsList(user?.organisationId as string),
    donationQueryParams
  )

  useDidMountEffect(() => query(donationQueryParams), [donationQueryParams, query])

  const [{ loading: csvDownloaderLoading, errorCSVMessage }, { fetchDonationCSV }] = useCSVDownloader({
    service: getDonationReport(user?.organisationId as string),
    fileName: csvFileName,
    successMessage: t('toast.donationsListExportSuccess')
  })

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    anchorEl.current = event.currentTarget
    setIsDatePickerOpened(!isDatePickerOpened)
  }

  const handleExport = () => {
    if (fetchDonationCSV) {
      fetchDonationCSV({
        ...formatDateParams(queryParams.startDate, queryParams.endDate),
        hasGiftNote: showDonationsWithGiftNotes || undefined
      })
    }
  }

  const handleClearDate = useCallback(() => {
    setQueryParams({ startDate: undefined, endDate: undefined })
  }, [setQueryParams])

  const columns: TableColumn[] = [
    { id: t('typography.donationsTableDonation'), isSortable: false, props: { width: '160px' } },
    { id: t('typography.donationsTableDonorName'), isSortable: false, props: { width: '160px' } },
    { id: t('typography.donationsTableDonorEmail'), isSortable: false },
    { id: t('typography.donationsTableGiftNote'), isSortable: false, props: { width: '160px' } },
    { id: t('typography.donationsTableMarketingConsent'), isSortable: false, props: { width: '160px' } },
    {
      id: t('typography.donationsTablePartner'),
      isSortable: false,
      props: { width: '160px' }
    },
    { id: t('typography.donationsTableStatus'), isSortable: false },
    { id: t('typography.donationsTableDonationCreated'), isSortable: false, props: { width: '160px' } },
    { id: '', content: '', isSortable: false }
  ]

  const determineFilterState = useMemo(() => {
    const hasDateFilter = queryParams.startDate && queryParams.endDate
    const hasGiftNoteFilter = showDonationsWithGiftNotes

    if (hasDateFilter && hasGiftNoteFilter) {
      return DONATION_FILTER_STATES.DATE_AND_GIFT_NOTE
    } else if (hasDateFilter) {
      return DONATION_FILTER_STATES.DATE_ONLY
    } else if (hasGiftNoteFilter) {
      return DONATION_FILTER_STATES.GIFT_NOTE_ONLY
    }
    return DONATION_FILTER_STATES.NONE
  }, [queryParams.startDate, queryParams.endDate, showDonationsWithGiftNotes])

  const tableEmptyStateConfig = useMemo(
    () => ({
      [DONATION_FILTER_STATES.NONE]: {
        title: t('typography.noDonations'),
        illustration: EMPTY_RESULT_STATES.NO_DONATIONS
      },
      [DONATION_FILTER_STATES.DATE_ONLY]: {
        title: t('typography.noDonationsInDateRange'),
        illustration: EMPTY_RESULT_STATES.NO_RESULTS
      },
      [DONATION_FILTER_STATES.GIFT_NOTE_ONLY]: {
        title: t('typography.noMatchingDonationResults'),
        illustration: EMPTY_RESULT_STATES.NO_RESULTS
      },
      [DONATION_FILTER_STATES.DATE_AND_GIFT_NOTE]: {
        title: t('typography.noMatchingDonationResults'),
        illustration: EMPTY_RESULT_STATES.NO_RESULTS
      }
    }),
    [t]
  )

  const getDonationStatusInformation = (status: DonationStatus) => {
    if (status === 'PLEDGED') {
      return t('typography.donationStatus.pledged.moreInformation')
    }
    if (status === 'PAID') {
      return t('typography.donationStatus.paid.moreInformation')
    }
    return null
  }

  const currentEmptyState = tableEmptyStateConfig[determineFilterState]

  if (isLoading) {
    return <Loader />
  }

  if (errorMessage || !!errorCSVMessage) {
    return (
      <div className={styles.errorMessage}>
        <ErrorView errorMessage={errorMessage || (errorCSVMessage as string)} />
      </div>
    )
  }

  return (
    <Grid gutter={12}>
      <Grid.Col>
        <DonationsPageHelpLinks />
      </Grid.Col>
      <Grid.Col>
        <TableProvider
          isLoading={false}
          totalResults={totalResults}
          columns={columns}
          emptyTableTitle={currentEmptyState.title}
          illustration={currentEmptyState.illustration}
        >
          <>
            <div className={styles.alertWrapper}>
              <Alert variant="info">{t('typography.donationsInfo')}</Alert>
            </div>
            <div className={styles.titleAndCTAs}>
              <Typography variant="h3">{t('typography.allDonations')}</Typography>
              <div className={styles.actionWrapper}>
                <Checkbox
                  name="giftNote"
                  active={showDonationsWithGiftNotes}
                  label={t('typography.giftNoteCheckbox')}
                  variant="default"
                  onChange={target => {
                    setShowDonationsWithGiftNotes(target.currentTarget.checked)
                  }}
                />
                <DateRange
                  open={isDatePickerOpened}
                  setOpen={setIsDatePickerOpened}
                  setQueryParams={setQueryParams}
                  queryParams={queryParams}
                  maxDate={new Date()}
                >
                  <Button
                    size="small"
                    type="submit"
                    variant={queryParams.startDate ? 'primary' : 'secondary'}
                    data-testid="show-date-range"
                    endIcon="chevron-down"
                    onClick={handleClick}
                  >
                    {t('typography.date')}{' '}
                    {((determineFilterState === DONATION_FILTER_STATES.DATE_ONLY ||
                      determineFilterState === DONATION_FILTER_STATES.DATE_AND_GIFT_NOTE) &&
                      `${dayJS(queryParams.startDate).format('ll')} - ${dayJS(queryParams.endDate).format('ll')}`) ||
                      t('typography.all')}
                  </Button>
                </DateRange>
                {(determineFilterState === DONATION_FILTER_STATES.DATE_ONLY ||
                  determineFilterState === DONATION_FILTER_STATES.DATE_AND_GIFT_NOTE) && (
                  <Typography className={styles.clearText} onClick={handleClearDate}>
                    {t('typography.clear')}
                  </Typography>
                )}
                <div className={styles.separator} />
                {csvDownloaderLoading ? (
                  <Button size="small" variant="secondary" data-testid="export-csv-loading">
                    {t('button.exporting')}
                  </Button>
                ) : (
                  <Button size="small" variant="secondary" data-testid="export-csv-btn" onClick={handleExport}>
                    {t('button.exportCsv')}
                  </Button>
                )}
              </div>
            </div>
          </>
          <Table>
            <TableBody>
              {data?.map(
                ({
                  amount,
                  createdAt,
                  currencyCode,
                  partner,
                  firstName,
                  lastName,
                  email,
                  consentedToBeContactedByOrg,
                  status,
                  disbursementId,
                  giftNote
                }: Donation) => (
                  <TableRow
                    key={`donation-list-${createdAt}`}
                    className={styles.listItem}
                    data-testid="donation-list-row"
                  >
                    <TableCell>
                      {formatAmount({
                        currencyCode,
                        value: formatMinorUnitsValueToFullNumberWithDecimals(
                          amount,
                          currencyInfo?.find(el => el.code === currencyCode)?.minorUnits as number
                        )
                      })}
                    </TableCell>
                    <TableCell>
                      {firstName ? (
                        <>
                          {firstName} {lastName}
                        </>
                      ) : (
                        <Badge variant="default" icon="view-off">
                          {t('typography.anonymous')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {email ? (
                        <div className={styles.email}>{email}</div>
                      ) : (
                        <Badge variant="default" icon="view-off">
                          {t('typography.anonymous')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography className={styles.giftNote}>
                        {giftNote && (
                          <Tooltip content={giftNote} trigger="hover">
                            <div className={styles.tooltipContent}>
                              <span className={styles.giftNoteText}>{giftNote}</span>
                              <Copy text={giftNote} className={styles.smallCopy} />
                            </div>
                          </Tooltip>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={consentedToBeContactedByOrg === 'yes' ? 'positive' : 'critical'}
                        icon={consentedToBeContactedByOrg === 'yes' ? 'approve' : 'reject'}
                      >
                        {t(`typography.${consentedToBeContactedByOrg === 'yes' ? 'yes' : 'no'}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className={styles.partnerName}>{partner.name}</div>
                    </TableCell>
                    <TableCell>
                      {
                        <Tooltip content={getDonationStatusInformation(status)} trigger="hover">
                          <div className={styles.statusBadge}>{getStatusBadge(status)}</div>
                        </Tooltip>
                      }
                    </TableCell>
                    <TableCell className={styles.created}>{getFormattedDate(createdAt)}</TableCell>
                    <TableCell className={styles.ellipsesCell}>
                      <Menu
                        title={<Icon name="menu-vertical" size={6} />}
                        sections={[
                          {
                            items: [
                              {
                                key: 'see-payout-batch',
                                label: t('typography.seePayoutBatch'),
                                shouldBeDisabled: !(disbursementId && status === 'PAID'),
                                as: 'button',
                                onClick: () => navigate(`${RoutePath.PAYOUTS}/${disbursementId}`)
                              }
                            ]
                          }
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
          <TablePagination
            totalResultsText={t('typography.results', {
              count: totalResults
            })}
            previousPage={previousPage}
            nextPage={nextPage}
          />
        </TableProvider>
      </Grid.Col>
    </Grid>
  )
}
